<template>
  <VContainer
    fluid
    class="pa-0 pb-12 mb-12"
  >
    <SLoadingOverlay :loading="pending" />
    <CmsContent :html-content="pageData && pageData.content" />
  </VContainer>
</template>


<script lang="ts" setup>
import CmsContent from '~/components/content/CmsContent.vue'
import { ContentPath } from '~/types/content'
import { useNavigationStore } from '~/stores/navigation'
import { useAuthStore } from '~/stores/auth'
import { useRootStore } from '~/stores/root'
import { UserRole } from '~/types/account'

const navigationStore = useNavigationStore()
const route = useRoute()
const contentUrl = `${ContentPath.FOOTER}${route.path}`

// Missing content is a 404, whether the content API says so itself or returns no
// body (the throw below). nuxt2 raised it through `context.error`, which swaps in the
// error page. A throw inside plain `useLazyAsyncData` only lands on its `error` ref,
// so an unknown /help, /company or /community slug rendered an empty page with HTTP
// 200. Other fetch failures stay local, as they did in nuxt2. AB#64219
//
// /legal is a deliberate difference: nuxt2's legal/_slug.vue never raised a 404 and
// rendered a blank page instead. It now 404s like the other prefixes.
const { pending } = useLazyAsyncDataOrErrorPage(route.path, async () => {
  const response = await navigationStore.fetchHtmlContent(contentUrl)
  if (!response?.content) {
    throw createError({ statusCode: 404, fatal: true })
  }
  return true
}, escalateMissingContent)
const pageData = computed(() => navigationStore.pageDataByKey(contentUrl))
// Reactive getters, the deprecated `keywords` field and the CMS `head` blob all live
// in the composable now that three pages need the same head — see
// app/composables/useCmsPageMeta.ts for why none of it can go through useSeoMeta
// alone. The `head` tag is new here: nuxt2's `pages/help/_slug.vue` emitted it and
// this port had dropped it. AB#63280
useCmsPageMeta(() => pageData.value)

// Ported from nuxt2 `pages/help/_slug.vue`. `middleware/role-guard.ts` bounces a
// disallowed user to `/help/unauthorized?redirect=<target>` (and the cart links to
// `/help/cart-unauthorized`); the landing page itself is what re-opens the sign-in
// dialog so an anonymous visitor can authenticate and be returned to where they were
// headed. Keyed on the path — same guard nuxt2 used — so the other CMS slug routes
// this component serves are unaffected.
const { user } = useSAuthState()
onMounted(() => {
  if (!route.path.includes('unauthorized')) {
    return
  }
  if (user.value && user.value.role !== UserRole.Anonymous) {
    return
  }
  const authStore = useAuthStore()
  const rootStore = useRootStore()
  rootStore.redirectRoute = typeof route.query.redirect === 'string' ? route.query.redirect : '/'
  authStore.updateAuthMenu(true)
})
</script>
